import { css } from "@emotion/css";
import { Carousel, useIllustrationThemeName } from "@octopusdeploy/design-system-components";
import { space, text } from "@octopusdeploy/design-system-tokens";
import { useTrackEvent } from "@octopusdeploy/portal-analytics";
import React from "react";
import { createChangeWelcomeDialogHelpPanelAnalyticsEvent } from "./Analytics/WelcomeDialogAnalytics";
import ImageConfigureDark from "./Images/Configure-dark.svg";
import ImageConfigureLight from "./Images/Configure-light.svg";
import ImageDeployDark from "./Images/Deploy-dark.svg";
import ImageDeployLight from "./Images/Deploy-light.svg";
import ImageMeasureDark from "./Images/Measure-dark.svg";
import ImageMeasureLight from "./Images/Measure-light.svg";
import ImageOperateDark from "./Images/Operate-dark.svg";
import ImageOperateLight from "./Images/Operate-light.svg";
import ImageReleaseDark from "./Images/Releases-dark.svg";
import ImageReleaseLight from "./Images/Releases-light.svg";
const helpImages = {
    configure: {
        light: ImageConfigureLight,
        dark: ImageConfigureDark,
    },
    deploy: {
        light: ImageDeployLight,
        dark: ImageDeployDark,
    },
    measure: {
        light: ImageMeasureLight,
        dark: ImageMeasureDark,
    },
    operate: {
        light: ImageOperateLight,
        dark: ImageOperateDark,
    },
    release: {
        light: ImageReleaseLight,
        dark: ImageReleaseDark,
    },
};
const helpPanelCarouselSteps = [
    <HelpPanelStep key="configure" title="Simplify your deployments" image={helpImages.configure} contentHeader="Easy setup" content="Configure your deployment process with our library of 500+ step templates. Or you can use command lines and scripts. Store your deployment process in Git using Config as Code."/>,
    <HelpPanelStep key="release" title="Simplify your deployments" image={helpImages.release} contentHeader="Repeatable deployments" content="Set once, deploy often. Define your deployment steps and create a new release to snapshot the process. You then use that same process consistently across your environments."/>,
    <HelpPanelStep key="deploy" title="Simplify your deployments" image={helpImages.deploy} contentHeader="Deploy anywhere" content="Octopus seamlessly connects to your deployment pipeline, letting you pull code from any build server or CI platform. Deploy anywhere – cloud, data centers, Kubernetes, on-premises servers."/>,
    <HelpPanelStep key="operate" title="Simplify your deployments" image={helpImages.operate} contentHeader="Automate ops tasks" content="Use Runbooks to automate routine and emergency operations tasks like infrastructure provisioning, database management, and website failover and restoration with one click."/>,
    <HelpPanelStep key="measure" title="Simplify your deployments" image={helpImages.measure} contentHeader="Measure performance" content="Octopus's DevOps Insights uses DORA metrics to tell you exactly how you're performing so you can find areas for improvement in your deployment processes."/>,
];
export function WelcomeDialogHelpPanelContent() {
    const trackEvent = useTrackEvent();
    return (<div className={styles.carouselWrapper}>
            <Carousel slides={helpPanelCarouselSteps} onInteraction={(slideKey: string) => trackEvent(createChangeWelcomeDialogHelpPanelAnalyticsEvent(slideKey))}/>
        </div>);
}
interface HelpPanelStepProps {
    title: string;
    image: {
        light: string;
        dark: string;
    };
    contentHeader: string;
    content: string;
}
function HelpPanelStep({ title, image, contentHeader, content }: HelpPanelStepProps) {
    const isDarkTheme = useIllustrationThemeName() === "dark";
    return (<div key={contentHeader} className={styles.helpPanelStep}>
            <div className={styles.title}>{title}</div>
            <img className={styles.image} src={isDarkTheme ? image.dark : image.light} alt={contentHeader}/>
            <div className={styles.contentContainer}>
                <div className={styles.contentHeader}>{contentHeader}</div>
                <div className={styles.content}>{content}</div>
            </div>
        </div>);
}
const styles = {
    carouselWrapper: css({
        paddingBottom: space[24],
        marginTop: "auto",
        marginBottom: "auto",
        height: "100%",
    }),
    helpPanelStep: css({ display: "flex", flexDirection: "column" }),
    title: css({ font: text.body.regular.large, marginBottom: space[24] }),
    image: css({ maxWidth: "456px", margin: "0 auto" }),
    contentContainer: css({ display: "flex", flexDirection: "column", gap: space[4] }),
    contentHeader: css({ font: text.heading.medium, marginTop: space[12] }),
    content: css({ font: text.body.regular.large }),
};
